// frontend/src/features/campaign/utils/outreachInterpolation.js

/**
 * Strips any non-digit and non-plus characters from phone numbers
 */
export function cleanPhoneNumber(phone = '') {
  return String(phone || '').replace(/[^0-9+]/g, '');
}

/**
 * Replaces personalization template tokens with safe, real prospect and sender data.
 * Supports {{firstName}}, {firstName}, {{first_name}}, {{companyName}}, {{senderName}}, etc.
 */
export function interpolateTokens(template = '', lead = {}, senderName = 'Sales Representative') {
  if (!template) return '';
  const safeLead = lead || {};
  const fullName =
    safeLead.name ||
    safeLead.fullName ||
    (safeLead.firstName ? `${safeLead.firstName} ${safeLead.lastName || ''}`.trim() : 'there');
  const firstName =
    safeLead.firstName || (fullName && fullName !== 'there' ? fullName.split(' ')[0] : 'there');
  const lastName =
    safeLead.lastName ||
    (fullName && fullName.includes(' ') ? fullName.split(' ').slice(1).join(' ') : '');
  const companyName =
    safeLead.company || safeLead.companyName || safeLead.organization || 'your company';
  const jobTitle = safeLead.jobTitle || safeLead.title || 'Leader';

  return template
    .replace(/\{{1,2}\s*firstName\s*\}{1,2}/gi, firstName)
    .replace(/\{{1,2}\s*first_name\s*\}{1,2}/gi, firstName)
    .replace(/\{{1,2}\s*lastName\s*\}{1,2}/gi, lastName)
    .replace(/\{{1,2}\s*last_name\s*\}{1,2}/gi, lastName)
    .replace(/\{{1,2}\s*fullName\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*full_name\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*name\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*prospectName\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*prospect_name\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*recipientName\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*recipient_name\s*\}{1,2}/gi, fullName)
    .replace(/\{{1,2}\s*companyName\s*\}{1,2}/gi, companyName)
    .replace(/\{{1,2}\s*company_name\s*\}{1,2}/gi, companyName)
    .replace(/\{{1,2}\s*company\s*\}{1,2}/gi, companyName)
    .replace(/\{{1,2}\s*jobTitle\s*\}{1,2}/gi, jobTitle)
    .replace(/\{{1,2}\s*title\s*\}{1,2}/gi, jobTitle)
    .replace(/\{{1,2}\s*senderName\s*\}{1,2}/gi, senderName)
    .replace(/\{{1,2}\s*sender_name\s*\}{1,2}/gi, senderName)
    .replace(/\{{1,2}\s*sender\s*\}{1,2}/gi, senderName);
}

/**
 * Builds a Gmail Web compose URL with sender account isolation.
 * Uses `authuser` query parameter to force Google Mail to open the compose session
 * using the specified sender account (e.g. SMTP_USER), preventing accidental
 * dispatch from a user's personal default Gmail account.
 */
export function buildIsolatedGmailComposeUrl({
  to = '',
  subject = '',
  body = '',
  senderEmail = '',
}) {
  const params = new URLSearchParams({
    view: 'cm',
    fs: '1',
    to: to || '',
    su: subject || '',
    body: body || '',
  });

  const cleanSender = String(senderEmail || '').trim();
  if (cleanSender && cleanSender.includes('@')) {
    params.set('authuser', cleanSender);
    return `https://mail.google.com/mail/u/?${params.toString()}`;
  }

  return `https://mail.google.com/mail/?${params.toString()}`;
}

/**
 * Builds a direct WhatsApp click-to-chat URL (wa.me) for 1-click browser navigation.
 * Strips non-digits and leading '+' sign, and encodes message text.
 * Returns empty string if phone is missing or contains no digits.
 */
export function buildWhatsAppClickToChatUrl({ phone = '', message = '' } = {}) {
  const digitsOnly = cleanPhoneNumber(phone).replace(/^\+/, '');
  if (!digitsOnly) return '';

  const encodedMsg = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${digitsOnly}${encodedMsg}`;
}

/**
 * Ensures a generated outreach template retains dynamic placeholders {{firstName}} and {{companyName}}
 * instead of baking in hardcoded lead details from the sample/first lead.
 * This guarantees that subsequent leads in a cohort get personalized with their own details.
 */
export function ensureTemplateTokens(template = '', sampleLead = {}) {
  if (!template || typeof template !== 'string') return '';
  let result = template;
  const safeLead = sampleLead || {};

  const fullName = safeLead.name || safeLead.fullName || '';
  const firstName =
    safeLead.firstName || (fullName && fullName !== 'there' ? fullName.split(' ')[0] : '');
  const companyName = safeLead.company || safeLead.companyName || safeLead.organization || '';

  // Check if first name token is missing but sample first name is present
  const hasFirstNameToken = /\{{1,2}\s*(firstName|first_name)\s*\}{1,2}/i.test(result);
  if (!hasFirstNameToken) {
    if (fullName && fullName.length > 2) {
      const escapedFull = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(`\\b${escapedFull}\\b`, 'g'), '{{firstName}}');
    }
    if (firstName && firstName.length > 1 && firstName.toLowerCase() !== 'there') {
      const escapedFirst = firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(`\\b${escapedFirst}\\b`, 'g'), '{{firstName}}');
    }
  }

  // Check if company token is missing but sample company name is present
  const hasCompanyToken = /\{{1,2}\s*(companyName|company_name|company)\s*\}{1,2}/i.test(result);
  if (
    !hasCompanyToken &&
    companyName &&
    companyName.length > 1 &&
    companyName.toLowerCase() !== 'your company'
  ) {
    const escapedCompany = companyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escapedCompany, 'g'), '{{companyName}}');
  }

  return result;
}

export const LINKEDIN_NOTE_CHAR_LIMIT = 300;
export const LINKEDIN_INMAIL_SUBJECT_LIMIT = 200;

/**
 * Validates and formats a LinkedIn profile URL, or generates a targeted search query fallback.
 * Accepts full URLs (https://www.linkedin.com/in/...), relative paths (in/username), or builds a
 * search fallback using prospect name and company if profile URL is missing.
 */
export function buildLinkedInProfileUrl({ linkedinUrl = '', name = '', company = '' } = {}) {
  const cleanUrl = String(linkedinUrl || '').trim();

  if (cleanUrl) {
    if (/^https?:\/\//i.test(cleanUrl)) {
      return cleanUrl;
    }
    if (/^(www\.)?linkedin\.com/i.test(cleanUrl)) {
      return `https://${cleanUrl.replace(/^\/+/, '')}`;
    }
    if (cleanUrl.startsWith('in/') || cleanUrl.startsWith('/in/')) {
      const cleanPath = cleanUrl.replace(/^\/+/, '');
      return `https://www.linkedin.com/${cleanPath}`;
    }
    // If it's a handle or identifier without slashes
    if (!cleanUrl.includes('/') && !cleanUrl.includes(' ')) {
      return `https://www.linkedin.com/in/${cleanUrl}`;
    }
  }

  // Fallback: targeted LinkedIn search query
  const queryParts = [name, company].filter(Boolean).map((s) => String(s).trim());
  if (queryParts.length > 0) {
    return `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(
      queryParts.join(' ')
    )}`;
  }

  return 'https://www.linkedin.com';
}

/**
 * Truncates a LinkedIn connection request note to fit within LinkedIn's strict character limit.
 * Tries to break at word boundaries and appends ellipsis if truncated.
 */
export function truncateForLinkedInNote(message = '', maxChars = LINKEDIN_NOTE_CHAR_LIMIT) {
  if (!message || typeof message !== 'string') return '';
  const trimmed = message.trim();
  if (trimmed.length <= maxChars) return trimmed;

  const slice = trimmed.slice(0, maxChars - 3);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > maxChars * 0.7) {
    return `${slice.slice(0, lastSpace).trim()}...`;
  }
  return `${slice.trim()}...`;
}

export const DEFAULT_LINKEDIN_NOTE =
  'Hi {{firstName}}, noticed your leadership in sales at {{companyName}}. We help B2B teams accelerate outbound pipeline with verified direct dials. Would love to connect!';

export const DEFAULT_LINKEDIN_INMAIL_BODY =
  'Hi {{firstName}},\n\nI noticed your leadership at {{companyName}}. In relation to accelerating revenue pipeline, we help teams drive validated 3.5x growth with verified decision-maker direct dials.\n\nWould you be open to a brief 15-minute consultation this week to explore how this applies to {{companyName}}?\n\nBest regards,\n{{senderName}}';

/**
 * Cross-platform clipboard copy helper with robust multi-strategy fallback:
 * 1. Attempts modern navigator.clipboard.writeText (when supported, in secure context, and active)
 * 2. Falls back to synchronous document.execCommand('copy') with temporary textarea
 * Returns Promise<boolean> indicating whether the write to clipboard succeeded.
 */
export async function copyTextToClipboard(text = '') {
  if (!text || typeof text !== 'string') return false;

  // Strategy 1: Async Clipboard API (works on HTTPS & localhost when document is active)
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn(
        '[Clipboard] navigator.clipboard.writeText failed or rejected, trying execCommand fallback:',
        err?.message || err
      );
    }
  }

  // Strategy 2: Synchronous document.execCommand('copy') via temporary textarea
  if (typeof document !== 'undefined' && typeof document.execCommand === 'function') {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        return true;
      }
    } catch (err) {
      console.warn('[Clipboard] execCommand fallback failed:', err?.message || err);
    }
  }

  return false;
}

/**
 * Formats a phone string into an RFC 3966 `tel:` URI for native device dialers.
 * Returns empty string if phone is missing or contains no digits.
 */
export function buildTelUri(phone = '') {
  const digits = cleanPhoneNumber(phone);
  if (!digits) return '';
  return `tel:${digits}`;
}

/**
 * Builds a direct WhatsApp Web chat URL bypassing intermediate wa.me landing pages.
 */
export function buildWhatsAppDirectChatUrl(phone = '') {
  const digits = cleanPhoneNumber(phone);
  if (!digits) return '';
  const cleanDigits = digits.replace(/^\+/, '');
  return `https://web.whatsapp.com/send?phone=${cleanDigits}`;
}

/**
 * Builds a direct WhatsApp Desktop application URI scheme (whatsapp://send?phone=...)
 */
export function buildWhatsAppDesktopUri(phone = '') {
  const digits = cleanPhoneNumber(phone);
  if (!digits) return '';
  const cleanDigits = digits.replace(/^\+/, '');
  return `whatsapp://send?phone=${cleanDigits}`;
}

/**
 * Builds a QR code image URL encoding `tel:<phone>` or `https://wa.me/<phone>` so users on desktop computers
 * without a SIM card can point their mobile phone camera at the screen to dial immediately.
 */
export function buildQrCodeUrl(phone = '', size = 150, mode = 'tel') {
  const digits = cleanPhoneNumber(phone);
  if (!digits) return '';
  let payloadUri = '';
  if (mode === 'whatsapp') {
    const cleanDigits = digits.replace(/^\+/, '');
    payloadUri = `https://wa.me/${cleanDigits}`;
  } else {
    payloadUri = buildTelUri(phone);
  }
  if (!payloadUri) return '';
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(payloadUri)}&size=${size}x${size}`;
}

export const DEFAULT_PHONE_BATTLECARD = {
  opener:
    "Hi {{firstName}}, this is {{senderName}} with {{product}}. I know I caught you unannounced—do you have 30 seconds for me to share why I called, and you can let me know if it's relevant to {{companyName}}?",
  valueProp:
    'We help teams like {{companyName}} drive validated 3.5x pipeline acceleration with verified decision-maker direct dials, eliminating manual prospecting research and low connection rates.',
  qualifyingQuestions: [
    'How is your team currently tackling outbound pipeline predictability and connect rates at {{companyName}}?',
    'What is your revenue and meetings target for key accounts this quarter?',
    'Who else on your leadership team evaluates outbound revenue and sales intelligence solutions?',
  ],
  objectionHandlers: {
    competitor:
      "That's fantastic—most teams we partner with were using tools like ZoomInfo or Apollo too. Where they saw an instant 3.5x lift was in local APAC direct dials and verified WhatsApp numbers where generic lists fail.",
    sendEmail:
      "I'd be glad to send an email! To make sure it's genuinely useful for {{companyName}}, which metric matters most to you right now: verified connect rates or qualified meeting volume?",
    noBudget:
      "Understood. We're not asking for budget today—we're strictly exploring if there is a mutual operational fit for your next planning cycle. Would 15 minutes this Thursday make sense?",
    notInterested:
      'Fair enough. Is outbound pipeline growth completely solved for your team right now, or is the timing just tight this week?',
    badTiming:
      'Totally understand you are busy right now. Would next Tuesday morning or Thursday afternoon be better for a brief 5-minute reconnect?',
  },
  voicemailScript:
    "Hi {{firstName}}, this is {{senderName}} calling regarding {{companyName}}. I noticed your team's focus on revenue growth and wanted to share how we help sales leaders 3.5x meetings with verified decision-maker direct dials. I'll drop a quick note to {{email}} as well. My direct number is {{senderPhone}}. Have a great day!",
};

export const CALL_DISPOSITIONS = [
  {
    id: 'meeting_booked',
    label: 'Meeting Booked',
    icon: 'fas fa-calendar-check',
    color: '#10b981',
    category: 'positive',
  },
  {
    id: 'follow_up',
    label: 'Follow-up Required',
    icon: 'fas fa-clock',
    color: '#3b82f6',
    category: 'positive',
  },
  {
    id: 'voicemail',
    label: 'Left Voicemail',
    icon: 'fas fa-voicemail',
    color: '#8b5cf6',
    category: 'neutral',
  },
  {
    id: 'gatekeeper',
    label: 'Gatekeeper / Busy',
    icon: 'fas fa-user-shield',
    color: '#f59e0b',
    category: 'neutral',
  },
  {
    id: 'no_answer',
    label: 'No Answer / Ringing',
    icon: 'fas fa-phone-slash',
    color: '#64748b',
    category: 'neutral',
  },
  {
    id: 'not_interested',
    label: 'Not Interested',
    icon: 'fas fa-times-circle',
    color: '#ef4444',
    category: 'negative',
  },
  {
    id: 'do_not_call',
    label: 'Do Not Call (DNC)',
    icon: 'fas fa-ban',
    color: '#dc2626',
    category: 'negative',
  },
];
